import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  buttonClasses,
  EmptyValue,
  LoadError,
  PageTitle,
  Skeleton,
  StatusChip,
} from "@/components/ui";
import { useCustomerViolation } from "@/hooks/use-customer-violations";
import { formatLongDate } from "@/lib/dates";
import { deviceLink, issuesLink } from "@/lib/destinations";
import { isResolved, violationStatusKey } from "@/lib/issue-list";
import { present } from "@/lib/present";
import { uuidParam } from "@/lib/search-params";

export const Route = createFileRoute("/_customer/letters/issues/$violationId")({
  component: IssuePage,
});

const PAGE = "flex flex-col gap-5 lg:gap-6";
const TERM = "border-b border-line py-2.25 pr-5.5 text-sm text-muted";
const VALUE = "border-b border-line py-2.25 text-sm font-medium text-ink";

// The loaded page's shape in static blocks: the title and its line, then the record.
const LOADING = (
  <div className={PAGE}>
    <div>
      <Skeleton height={34} width="55%" />
      <Skeleton height={18} width="30%" className="mt-2" />
    </div>
    <Skeleton variant="card" height={240} />
  </div>
);

/**
 * One compliance issue: its type in plain words over its property, then the
 * record: where it stands, when it was opened, the day to resolve it by, the
 * day it was resolved, the property, the device and the details. An id that
 * cannot be an issue is not found without a read.
 */
function IssuePage() {
  const { t, i18n } = useTranslation("customer");
  const { violationId } = Route.useParams();
  const id = uuidParam(violationId) ?? "";
  const read = useCustomerViolation(id);
  const violation = read.data;

  if (!id || read.error?.status === 404) {
    return (
      <div
        role="alert"
        className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5"
      >
        <h1 className="text-lg font-semibold tracking-[-0.01em] text-ink">
          {t("issueDetail.notFound")}
        </h1>
        <Link {...issuesLink()} className={`mt-3 ${buttonClasses("secondary")}`}>
          {t("nav.tabs.issues")}
        </Link>
      </div>
    );
  }

  if (read.isError) {
    return (
      <LoadError
        asPage
        title={t("issueDetail.error")}
        body={t("loadError.body")}
        onRetry={() => void read.refetch()}
      />
    );
  }

  if (!violation) return LOADING;

  const title = t(`violations.type.${violation.violation_type}`);
  const property = present(violation.facility_name);
  const resolved = isResolved(violation.status);
  const day = (value: string) => formatLongDate(value, i18n.language);
  const device = violation.device_installation_id;
  const serial = present(violation.device_serial_number);
  const description = present(violation.description);

  return (
    <div className={PAGE}>
      <PageTitle
        title={title}
        sub={property ?? undefined}
        back={{ label: t("nav.tabs.issues"), link: issuesLink() }}
        trail={[{ label: t("nav.tabs.issues"), link: issuesLink() }, { label: title }]}
      />
      <section className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5">
        <StatusChip
          tone={resolved ? "green" : "red"}
          label={t(violationStatusKey(violation.status))}
        />
        <dl className="mt-3.5 grid grid-cols-[auto_1fr] border-t border-line">
          <Row label={t("issueDetail.opened")} value={day(violation.discovered_date)} />
          <Row label={t("issueDetail.due")} value={day(violation.correction_due_date)} />
          {violation.resolution_date && (
            <Row label={t("issueDetail.resolved")} value={day(violation.resolution_date)} />
          )}
          <Row label={t("issueDetail.property")} value={property} />
          {device && (
            <Row
              label={t("issueDetail.device")}
              value={
                <Link
                  {...deviceLink(device)}
                  className="tracking-[0.02em] text-brand-fg tabular-nums underline-offset-2 hover:underline"
                >
                  {serial ?? t("deviceRow.unnamed")}
                </Link>
              }
            />
          )}
        </dl>
        {description && (
          <div className="mt-4">
            <h2 className="text-sm text-muted">{t("issueDetail.description")}</h2>
            <p className="mt-1 text-sm whitespace-pre-wrap text-ink">{description}</p>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className={TERM}>{label}</dt>
      <dd className={VALUE}>{value ?? <EmptyValue />}</dd>
    </>
  );
}
